// Buyer guides: the typed content behind GuidePage. Landing metadata, the prerender and the
// footer all read this list, so a new guide is one entry here plus its sitemap line
// (and `/guias` already sits in SPA_ROOT_SEGMENTS, backend/core/spa.py).

import type { GuideIllustrationId } from './GuideIllustrations';

export type GuideLink = { text: string; href: string };
export type GuideInline = string | GuideLink;
export type GuideParagraph = GuideInline[];
export type GuideTable = { caption: string; headers: string[]; rows: string[][] };
export type GuideCallout = { statement?: string; figures?: Array<{ value: string; label: string }> };
export type GuideSection = {
  heading: string;
  /** Anchor for the page index. */
  id?: string;
  /** Short name in the page index, for guides with one. */
  shortLabel?: string;
  illustration?: GuideIllustrationId;
  paragraphs?: GuideParagraph[];
  checklist?: string[];
  table?: GuideTable;
  /** Full-width break band shown after this section. */
  callout?: GuideCallout;
};
export type GuideFaq = { question: string; answer: string };

export type Guide = {
  path: string;
  /** Short label for the footer link. */
  navLabel: string;
  title: string;
  description: string;
  h1: string;
  lede: string;
  /** Omitted: the zoning plan drawing from the landing page. */
  heroIllustration?: GuideIllustrationId;
  /** Shows the interactive costs calculator right after the opening. */
  hasCalculator?: boolean;
  /** ISO date of the last human review, shown on the page and in Article schema. */
  reviewedOn: string;
  sections: GuideSection[];
  faq: GuideFaq[];
  sources: GuideLink[];
  ctaTitle: string;
  ctaBody: string;
};

const LITERACIA_COSTS = 'https://www.literaciafinanceira.pt/artigos/quanto-custa-comprar-casa';
const ECO_DSTI = 'https://eco.sapo.pt/2026/07/02/banco-de-portugal-aperta-regras-do-credito-a-partir-de-agosto/';
const DF_ENERGY = 'https://www.doutorfinancas.pt/energia-e-telecomunicacoes/certificado-energetico-como-obter-e-como-afeta-a-compra-de-casa/';
const GOV_GUIDE = 'https://www.gov.pt/guias/compra-e-venda-de-imoveis-em-portugal-cidadaos-europeus';

const LITERACIA_SIMULATOR = 'https://www.literaciafinanceira.pt/simulador-imt-imposto-selo';
const APCMC_TABLES = 'https://apcmc.pt/legislacao/imt-tabelas-praticas-em-vigor-2026/';
const CIS_ART_7A = 'https://info.portaldasfinancas.gov.pt/pt/informacao_fiscal/codigos_tributarios/selo/Pages/selo7a.aspx';

export const CHECKLIST_PATH = '/guias/checklist-visita-casa';
export const COSTS_PATH = '/guias/quanto-custa-comprar-casa';

export const GUIDES: Guide[] = [
  {
    path: '/guias/como-saber-se-uma-casa-e-boa',
    navLabel: 'Como saber se uma casa é boa para ti',
    title: 'Como saber se uma casa é boa para ti: 7 critérios | miD',
    description: 'Sete critérios para decidir se uma casa te serve: custo total, rotina, espaço, estado, ruído, documentos e aquilo de que abdicas.',
    h1: 'Como saber se uma casa é boa para ti',
    lede: 'Uma casa é boa para ti quando cabe no custo total que consegues pagar, serve a tua rotina e continua a fazer sentido nas escolhas difíceis. O anúncio não te diz isto.',
    reviewedOn: '2026-10-31',
    sections: [
      {
        heading: '1. Cabe no custo total, não só no preço',
        id: 'custo-total',
        shortLabel: 'Custo total',
        illustration: 'costStack',
        callout: {
          figures: [
            { value: '3% a 5%', label: 'do preço em impostos e custos, além da entrada' },
            { value: '45%', label: 'limite geral recomendado para a taxa de esforço, desde agosto de 2026' },
          ],
        },
        paragraphs: [
          ['O preço do anúncio não é o que vais pagar. Além da entrada, há IMT, Imposto do Selo, escritura, registos e custos do banco. A Literacia Financeira aponta para ', { text: 'cerca de 3% a 5% do preço em impostos e custos', href: LITERACIA_COSTS }, ', além da entrada. Podes ', { text: 'calcular o teu caso aqui', href: COSTS_PATH }, '.'],
          ['Depois, olha para a prestação. Para créditos avaliados desde 1 de agosto de 2026, o Banco de Portugal recomenda ', { text: 'um limite geral de 45% para a taxa de esforço', href: ECO_DSTI }, ', com uma margem limitada de exceções. Esse é um limite, não um objetivo: conta também com condomínio, seguros e IMI.'],
        ],
      },
      {
        heading: '2. Serve a tua rotina',
        id: 'rotina',
        shortLabel: 'Rotina',
        illustration: 'commute',
        paragraphs: [
          ['Faz o trajeto até ao trabalho à hora a que o fazes, não ao domingo de manhã. Vê também a escola, os transportes e as compras do dia a dia. Uma casa mais barata pode sair cara em tempo.'],
        ],
      },
      {
        heading: '3. Tem espaço para quem lá vai viver',
        id: 'espaco',
        shortLabel: 'Espaço',
        illustration: 'floorPlan',
        paragraphs: [
          ['Conta quem vive contigo hoje e quem pode viver contigo daqui a cinco anos. Compara a área útil, não só a tipologia: dois T2 podem ser muito diferentes. Pergunta-te onde trabalhas, onde guardas as coisas e onde estacionas.'],
        ],
      },
      {
        heading: '4. O estado da casa está claro',
        id: 'estado',
        shortLabel: 'Estado',
        illustration: 'energy',
        callout: { statement: 'As fotografias mostram a casa. A visita mostra como é lá viver.' },
        paragraphs: [
          ['A classe energética tem de constar do anúncio, numa ', { text: 'escala que vai de A+ a F', href: DF_ENERGY }, '. Uma classe baixa pode significar contas mais altas e menos conforto. Se a casa precisa de obras, pede um orçamento antes de decidir e soma-o ao custo total.'],
        ],
      },
      {
        heading: '5. Ruído, luz e exterior passam na visita',
        id: 'ruido-e-luz',
        shortLabel: 'Ruído e luz',
        illustration: 'lightAndNoise',
        paragraphs: [
          ['As fotografias não mostram o barulho da rua nem o sol ao fim do dia. Visita a casa a horas diferentes e vê para onde viram as janelas. A ', { text: 'checklist para visitar uma casa', href: CHECKLIST_PATH }, ' ajuda-te a não esquecer nada.'],
        ],
      },
      {
        heading: '6. Os documentos batem certo',
        id: 'documentos',
        shortLabel: 'Documentos',
        illustration: 'documents',
        paragraphs: [
          ['Antes de assinares, pede ', { text: 'a caderneta predial, a certidão do registo predial, a licença de utilização, a ficha técnica, o certificado energético e a declaração de não dívida ao condomínio', href: GOV_GUIDE }, '. Confirma que a área, o dono e os encargos coincidem com o que te disseram. Com dúvidas, fala com um solicitador ou um advogado antes do contrato-promessa.'],
        ],
      },
      {
        heading: '7. Sabes do que estás a abdicar',
        id: 'abdicar',
        shortLabel: 'O que cedes',
        illustration: 'tradeOff',
        paragraphs: [
          ['Nenhuma casa tem tudo. Decide antes o que não negocias e o que podes ceder: mais espaço ou uma zona melhor? Se a tua lista muda a cada visita, estás a comparar casas com critérios diferentes.'],
        ],
      },
      {
        heading: 'Como o miD te ajuda a responder',
        illustration: 'match',
        paragraphs: [
          ['No Buyer DNA defines, numa conversa, quem vai viver contigo, o orçamento, as zonas e aquilo de que não abdicas. Depois colas o link de um anúncio de um portal suportado no Matchmaker. Ficas com o Match em percentagem, o que encaixa, o que choca e uma estimativa do que vais pagar. Não substitui a visita nem um solicitador: serve para decidires a que casas vale a pena ir.'],
        ],
      },
    ],
    faq: [
      {
        question: 'O que devo ver primeiro numa casa?',
        answer: 'O custo total. Se a casa não cabe no que consegues pagar com impostos, custos e prestação incluídos, os outros critérios deixam de contar.',
      },
      {
        question: 'Posso analisar um anúncio do Idealista no miD?',
        answer: 'Não. O Matchmaker lê portais suportados, como Imovirtual, Casa Yes, RE/MAX, ERA ou Century 21, e a disponibilidade da análise depende do anúncio. O Idealista não é suportado.',
      },
      {
        question: 'O miD é gratuito?',
        answer: 'Sim, para quem procura casa. O Buyer DNA e o Matchmaker não te custam nada, e começas sem criar conta.',
      },
    ],
    sources: [
      { text: 'Literacia Financeira: quanto custa comprar casa', href: LITERACIA_COSTS },
      { text: 'ECO: Banco de Portugal aperta regras do crédito a partir de agosto (2 de julho de 2026)', href: ECO_DSTI },
      { text: 'Doutor Finanças: certificado energético', href: DF_ENERGY },
      { text: 'gov.pt: compra e venda de imóveis em Portugal', href: GOV_GUIDE },
    ],
    ctaTitle: 'Vê se uma casa te serve a ti',
    ctaBody: 'Cria o teu Buyer DNA, sem conta, e compara o próximo anúncio com o que procuras.',
  },
  {
    path: COSTS_PATH,
    navLabel: 'Quanto custa comprar casa em Portugal',
    title: 'Quanto custa comprar casa em Portugal: calculadora | miD',
    description: 'Calcula o IMT, o Imposto do Selo, a escritura e os registos de uma casa em Portugal, com as tabelas de 2026 e o IMT Jovem.',
    h1: 'Quanto custa comprar casa em Portugal',
    lede: 'Além da entrada, comprar casa custa impostos, escritura, registos e custos do banco. Por exemplo, uma casa de 250.000 € para habitação própria e permanente paga 7.042 € de IMT e 2.000 € de Imposto do Selo, ou nada disto se tiveres direito a IMT Jovem.',
    heroIllustration: 'costStack',
    hasCalculator: true,
    reviewedOn: '2026-10-31',
    sections: [
      {
        heading: 'IMT',
        paragraphs: [
          ['O IMT é o imposto sobre a compra. Depende do valor, do destino da casa e da região, e incide sobre o valor mais alto entre o preço e o VPT, o valor patrimonial tributário que consta da caderneta predial. Estes são os ', { text: 'escalões de 2026 no Continente', href: APCMC_TABLES }, ' para habitação própria e permanente:'],
        ],
        table: {
          caption: 'IMT 2026, habitação própria e permanente, Continente',
          headers: ['Valor', 'Taxa', 'Parcela a abater'],
          rows: [
            ['Até 106.346 €', '0%', '0 €'],
            ['Até 145.470 €', '2%', '2.126,92 €'],
            ['Até 198.347 €', '5%', '6.491,02 €'],
            ['Até 330.539 €', '7%', '10.457,96 €'],
            ['Até 660.982 €', '8%', '13.763,35 €'],
            ['Até 1.150.853 €', '6% (taxa única)', '0 €'],
            ['Acima de 1.150.853 €', '7,5% (taxa única)', '0 €'],
          ],
        },
      },
      {
        heading: 'Imposto do Selo',
        illustration: 'stampDuty',
        paragraphs: [
          ['Na compra pagas ', { text: '0,8% sobre o valor de referência', href: LITERACIA_SIMULATOR }, ', o mais alto entre o preço e o VPT. Com crédito, pagas também ', { text: '0,6% sobre o valor do empréstimo', href: LITERACIA_COSTS }, ', em prazos de 5 anos ou mais.'],
        ],
      },
      {
        heading: 'Escritura e registos',
        illustration: 'deedCosts',
        paragraphs: [
          ['No balcão Casa Pronta, os ', { text: 'preços fixos são 375 € sem crédito e 700 € com crédito', href: LITERACIA_COSTS }, '. Num notário ou solicitador, os honorários andam normalmente entre 200 € e 500 €, e os registos pagam-se à parte.'],
        ],
      },
      {
        heading: 'Custos do banco',
        illustration: 'bankCosts',
        paragraphs: [
          ['Se pedires crédito, conta com ', { text: 'avaliação (200 € a 350 €), dossier (200 € a 300 €) e formalização (200 € a 725 €)', href: LITERACIA_COSTS }, '. Estes valores variam de banco para banco e não entram na calculadora.'],
        ],
      },
      {
        heading: 'IMT Jovem',
        illustration: 'imtJovem',
        callout: {
          figures: [
            { value: '35 anos', label: 'é a idade limite, inclusive, na data da escritura' },
            { value: '330.539 €', label: 'até este valor, quem tem direito não paga IMT' },
          ],
        },
        paragraphs: [
          ['Até aos 35 anos, inclusive, na primeira habitação própria e permanente, ', { text: 'não pagas IMT até 330.539 €', href: LITERACIA_SIMULATOR }, '. Entre esse valor e 660.982 €, pagas 8% só sobre a parte que excede. Tens de ser residente fiscal, não ser dependente no IRS e não ter tido outra casa nos últimos três anos.'],
          ['No Imposto do Selo, a lei prevê ', { text: 'uma dedução até ao valor do imposto sobre o primeiro escalão', href: CIS_ART_7A }, ', por isso até 330.539 € não pagas, e acima disso pagas 0,8% sobre o excedente. Quem tem direito a IMT Jovem também não paga emolumentos.'],
        ],
      },
      {
        heading: 'Se não és residente fiscal',
        paragraphs: [
          ['As regras são diferentes e mudaram em 2026, por isso esta calculadora não cobre esse caso. Confirma o teu com um solicitador ou nas Finanças.'],
        ],
      },
    ],
    faq: [
      {
        question: 'Quanto custa comprar uma casa de 250.000 € em Portugal?',
        answer: 'Para habitação própria e permanente, sem IMT Jovem, 7.042,04 € de IMT e 2.000 € de Imposto do Selo, mais escritura e registos a partir de 375 €. Com crédito, somam-se 0,6% do empréstimo e os custos do banco.',
      },
      {
        question: 'Quem tem menos de 35 anos paga IMT?',
        answer: 'Na primeira habitação própria e permanente, quem cumpre as condições do IMT Jovem não paga IMT até 330.539 € e paga 8% só sobre a parte que excede esse valor, até 660.982 €.',
      },
      {
        question: 'O que é o VPT e porque conta?',
        answer: 'É o valor patrimonial tributário, que consta da caderneta predial. O IMT e o Imposto do Selo incidem sobre o valor mais alto entre o preço e o VPT, por isso um VPT acima do preço aumenta o imposto.',
      },
    ],
    sources: [
      { text: 'APCMC: tabelas práticas do IMT em vigor em 2026', href: APCMC_TABLES },
      { text: 'Literacia Financeira: simulador de IMT e Imposto do Selo', href: LITERACIA_SIMULATOR },
      { text: 'Literacia Financeira: quanto custa comprar casa', href: LITERACIA_COSTS },
      { text: 'Portal das Finanças: artigo 7.º-A do Código do Imposto do Selo', href: CIS_ART_7A },
    ],
    ctaTitle: 'Sabe quanto te custa cada casa que vês',
    ctaBody: 'Cria o teu Buyer DNA, sem conta, e o Matchmaker estima os custos de cada anúncio que analisares.',
  },
  {
    path: CHECKLIST_PATH,
    navLabel: 'Checklist para visitar uma casa',
    title: 'Checklist para visitar uma casa: o que ver e perguntar | miD',
    description: 'O que ver, testar e perguntar quando visitas uma casa para comprar: dentro de casa, no prédio, na zona e nos documentos. Pronta a imprimir.',
    h1: 'Checklist para visitar uma casa',
    heroIllustration: 'checklistSheet',
    lede: 'Numa visita de vinte minutos é fácil reparar na cozinha e esquecer o resto. Leva esta lista, imprime-a ou abre-a no telemóvel, e pergunta o que o anúncio não diz.',
    reviewedOn: '2026-10-31',
    sections: [
      {
        heading: 'Antes de ir',
        illustration: 'commute',
        checklist: [
          'Confirma o preço, a área e a tipologia do anúncio',
          'Faz o trajeto até ao trabalho no mapa, à tua hora de ponta',
          'Escreve o que não negocias e o que podes ceder',
          'Marca a visita de dia e, se puderes, uma segunda a outra hora',
        ],
      },
      {
        heading: 'Dentro de casa',
        illustration: 'floorPlan',
        callout: { statement: 'Uma visita de vinte minutos não chega para tudo. Por isso é que voltas a outra hora.' },
        checklist: [
          'Procura manchas, cheiro a mofo e humidade em cantos, tetos e armários',
          'Abre torneiras e vê a pressão e o tempo que a água quente demora',
          'Vê o quadro elétrico, as tomadas e o ano da última remodelação',
          'Abre janelas e estores, e ouve a casa com as janelas fechadas',
          'Vê para onde viram as janelas e a luz em cada divisão',
          'Mede o que te preocupa: o sofá, a cama, o espaço de trabalho',
        ],
      },
      {
        heading: 'Prédio e condomínio',
        illustration: 'building',
        checklist: [
          'Pergunta o valor do condomínio e se há quotas em dívida',
          'Pede as últimas atas e pergunta que obras estão previstas',
          'Vê o estado das partes comuns, do telhado e do elevador',
          'Confirma a garagem ou o lugar de estacionamento, e se ficam registados na compra',
        ],
      },
      {
        heading: 'Zona a horas diferentes',
        illustration: 'lightAndNoise',
        checklist: [
          'Volta à noite e ao fim de semana para ouvir o ruído e ver o movimento',
          'Vê o estacionamento na rua à hora a que chegas a casa',
          'Faz a pé o caminho até ao transporte, à escola e às compras',
          'Fala com um vizinho, se puderes',
        ],
      },
      {
        heading: 'Documentos a pedir',
        illustration: 'documents',
        checklist: [
          'Caderneta predial',
          'Certidão do registo predial',
          'Licença de utilização',
          'Ficha técnica da habitação',
          'Certificado energético',
          'Declaração de não dívida ao condomínio',
        ],
        paragraphs: [
          ['O ', { text: 'guia do gov.pt', href: GOV_GUIDE }, ' explica cada documento. A ficha técnica só se aplica se a licença de utilização foi emitida depois de 30 de março de 2004. Confirma com um solicitador antes do contrato-promessa.'],
        ],
      },
      {
        heading: 'Depois da visita',
        illustration: 'tradeOff',
        checklist: [
          'Escreve no mesmo dia o que gostaste e o que te preocupou',
          'Soma o custo total: impostos, escritura, banco e obras',
          'Compara com as outras casas pelos mesmos critérios',
        ],
      },
    ],
    faq: [
      {
        question: 'Quantas vezes devo visitar uma casa antes de comprar?',
        answer: 'Pelo menos duas, a horas diferentes. A primeira mostra a casa; a segunda mostra o ruído, a luz e o trajeto que não viste na primeira.',
      },
      {
        question: 'O que devo perguntar ao vendedor ou ao agente?',
        answer: 'Porque está à venda, há quanto tempo, que obras foram feitas, quanto custa o condomínio e se há obras previstas no prédio.',
      },
    ],
    sources: [
      { text: 'gov.pt: compra e venda de imóveis em Portugal', href: GOV_GUIDE },
    ],
    ctaTitle: 'Antes de marcares a visita, vê se vale a pena',
    ctaBody: 'Cria o teu Buyer DNA, sem conta, e compara o anúncio com o que procuras.',
  },
];

export const findGuide = (pathname: string): Guide | undefined =>
  GUIDES.find((guide) => guide.path === pathname.replace(/\/$/, ''));

const REVIEWED_MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** Fixed format, not toLocaleDateString: the prerender and the browser must print the same text. */
export const formatReviewedOn = (iso: string): string => {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} de ${REVIEWED_MONTHS[month - 1]} de ${year}`;
};
